import React, { useMemo, useState } from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import kebabCase from 'lodash/kebabCase.js';
import {
  Link,
  SelectGroupsInput,
  Spinner,
  TextInput,
  useAsync,
} from '@wix/cli-ui-kit';
import type { SelectGroup } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import type { BackOfficeExtensionContainer } from '@wix/extensions-data-client';
import { useExtensionDataClient } from '@wix/extensions-data-client';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useErrorReporter } from '@wix/cli-error-reporting';
import {
  useValidation,
  createDashboardPluginValidation,
} from '../../../../dev-center/components/validations/index.js';
import { generateDashboardPlugin } from './generate.js';
import { useGenerationTask } from '../../hooks/use-generation-task.js';
import { useProjectModel } from '../../../../model.js';
import { AppRequirement } from '../../AppRequirement.js';
import { useExtensions } from '../../../extensions/useExtensions.js';
import { useWixCliAppBi } from '../../../../bi/index.js';
import { InstallDependencies } from '../../InstallDependencies.js';
import { MultipleAppRequirement } from '../../MultipleAppRequirement.js';
import { DashboardPluginGeneratedNote } from './DashboardPluginGeneratedNote.js';
import type { SlotsApp } from '../DashboardPlugin.js';

const docsLink = (externalId?: string | null) =>
  new URL(
    `?portalSlug=sdk&externalId=${externalId}&fallbackUri=/build-apps/develop-your-app/extensions/dashboard-extensions/dashboard-plugins/supported-wix-app-dashboard-pages/about-dashboard-page-slots&platform=CLI`,
    'https://dev.wix.com/_api/docs-redirect/by-external-id'
  ).href;

export const DashboardPluginWizard: FC<{
  pluginsMap: Record<string, BackOfficeExtensionContainer>;
  slotsApp: SlotsApp;
}> = ({ slotsApp, pluginsMap }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const { addRequiredApp } = useDevCenterClient();
  const { getExtensions } = useExtensions();
  const { reportError } = useErrorReporter();
  const { getSdkSlotData } = useExtensionDataClient();

  const [backofficeExtensionWidget, setBackofficeExtensionWidget] =
    useState<BackOfficeExtensionContainer>();

  const extensionType = t(
    'generate_command.flow.dashboard_plugin'
  ).toLowerCase();

  const dashboardPlugins = getExtensions('DashboardPlugin');

  const [folder, setFolder] = useState<string>();
  const [title, setTitle] = useState<string>();
  const [requireAppsIds, setRequireAppsIds] = useState<string[]>();
  const [finishedInstallation, setFinishedInstallation] =
    useState<boolean>(false);

  const defaultFolderName = kebabCase(title);

  const validatePluginFolder = useValidation(
    createDashboardPluginValidation(dashboardPlugins)
  );

  const generationTask = useGenerationTask(async () => {
    if (backofficeExtensionWidget && folder && title && requireAppsIds) {
      const requireAppId = requireAppsIds.at(0);
      if (requireAppId) {
        await addRequiredApp({
          appId: model.config.appId,
          appIdToRequire: requireAppId,
        });
      }

      const sdkSlotData = await getSdkSlotData({
        slotId: backofficeExtensionWidget.component.componentId,
        appId: slotsApp.appId,
      }).catch((error: unknown) => {
        reportError(error);
      });

      return generateDashboardPlugin(
        model.projectFolder,
        backofficeExtensionWidget.component.componentId,
        folder,
        title,
        sdkSlotData?.importData
      );
    }
  }, [
    backofficeExtensionWidget?.component.componentId,
    folder,
    title,
    requireAppsIds,
  ]);

  const { status } = useAsync(async () => {
    const backofficeExtensionWidgets = Object.values(
      slotsApp.backofficePages.reduce<
        Record<string, SelectGroup<BackOfficeExtensionContainer>>
      >((extensionsByApps, backofficePage) => {
        extensionsByApps[backofficePage.component.componentId] ??= {
          title:
            backofficePage.component.data.backOfficePage.metaData?.title ??
            backofficePage.component.name,
          items: [],
        };

        for (const slotId of backofficePage.component.data.backOfficePage
          .slotIds) {
          const backofficeExtensionWidget = pluginsMap[slotId];
          if (backofficeExtensionWidget) {
            extensionsByApps[backofficePage.component.componentId]?.items.push({
              title:
                backofficeExtensionWidget.component.data
                  .backOfficeExtensionContainer.slotData?.displayName ??
                backofficeExtensionWidget.component.name,
              description: (
                <Trans
                  i18nKey="generate_command.dashboard_plugin.what_to_extend.item_description"
                  components={[
                    <Link
                      url={docsLink(
                        backofficeExtensionWidget.component.externalId
                      )}
                    />,
                  ]}
                />
              ),
              key: backofficeExtensionWidget.component.componentId,
              value: backofficeExtensionWidget,
            });
          }
        }

        return extensionsByApps;
      }, {})
    );

    for (const backofficeExtensionWidget of backofficeExtensionWidgets) {
      backofficeExtensionWidget.items.sort((a, b) =>
        a.title.localeCompare(b.title)
      );
    }

    backofficeExtensionWidgets.sort((a, b) => a.title.localeCompare(b.title));

    return backofficeExtensionWidgets;
  }, []);

  const task = useMemo(
    () =>
      match(generationTask, {
        Success: ({ hasUpdatedDependencies, newFiles }) => {
          if (!hasUpdatedDependencies) {
            setFinishedInstallation(true);
          }
          return (
            <>
              {hasUpdatedDependencies && (
                <InstallDependencies
                  onFinish={() => setFinishedInstallation(true)}
                />
              )}
              {finishedInstallation && backofficeExtensionWidget && (
                <DashboardPluginGeneratedNote
                  backofficeExtensionWidget={backofficeExtensionWidget}
                  generatedFiles={newFiles}
                />
              )}
            </>
          );
        },
        Loading: () => (
          <Spinner
            text={t('generate_command.generation.loading', {
              subject: extensionType,
            })}
          />
        ),
        Error: () => null,
        NotRequested: () => null,
      }),
    [
      generationTask,
      t,
      extensionType,
      finishedInstallation,
      backofficeExtensionWidget,
    ]
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({ result: backofficeExtensionWidgets }) => (
      <>
        <SelectGroupsInput<BackOfficeExtensionContainer>
          label={t('generate_command.dashboard_plugin.what_to_extend')}
          info={
            <Trans
              i18nKey="generate_command.dashboard_plugin.what_to_extend.description"
              // https://dev.wix.com/docs/build-apps/developer-tools/extensions/dashboard-extensions/dashboard-plugins/about-dashboard-plugin-extensions
              components={[<Link url="https://wix.to/ZeCt07g" />]}
            />
          }
          hideScroll
          groups={backofficeExtensionWidgets}
          onSubmit={(option) => {
            bi.cliFlowStepAnswered({
              question: t('generate_command.dashboard_plugin.what_to_extend'),
              questionKey: 'generate_command.dashboard_plugin.what_to_extend',
              answer: option.title,
              additionalInfo: { extensionType },
            });
            setBackofficeExtensionWidget(option.value);
          }}
        />
        {backofficeExtensionWidget &&
          (slotsApp.requiredApps.length === 1 ? (
            <AppRequirement
              appId={model.config.appId}
              requiredApps={[
                {
                  id: slotsApp.requiredApps[0].appId,
                  name: slotsApp.requiredApps[0].appName,
                },
              ]}
              onSubmit={setRequireAppsIds}
            />
          ) : (
            <MultipleAppRequirement
              appId={model.config.appId}
              requiredApps={slotsApp.requiredApps.map((requiredApp) => ({
                id: requiredApp.appId,
                name: requiredApp.appName,
              }))}
              onSubmit={setRequireAppsIds}
            />
          ))}
        {requireAppsIds && (
          <TextInput
            label={t('generate_command.dashboard_plugin.choose_title')}
            placeholder={t(
              'generate_command.dashboard_plugin.title_placeholder'
            )}
            onSubmit={(value) => {
              bi.cliFlowStepAnswered({
                question: t('generate_command.dashboard_plugin.choose_title'),
                questionKey: 'generate_command.dashboard_plugin.choose_title',
                answer: value,
                additionalInfo: { extensionType },
              });
              setTitle(value);
            }}
          />
        )}
        {title && (
          <TextInput
            label={t('generate_command.dashboard_plugin.choose_folder')}
            placeholder={defaultFolderName}
            onSubmit={(folder) => {
              bi.cliFlowStepAnswered({
                question: t('generate_command.dashboard_plugin.choose_folder'),
                questionKey: 'generate_command.dashboard_plugin.choose_folder',
                answer: folder,
                additionalInfo: { extensionType },
              });
              setFolder(folder);
            }}
            validate={validatePluginFolder}
          />
        )}
        {task}
      </>
    ),
  });
};
